import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { 
  PlayCircle, 
  CheckCircle, 
  AlertTriangle, 
  XCircle,
  TrendingUp,
  TrendingDown,
  Activity,
  BarChart3
} from 'lucide-react';
import { blink } from '@/lib/blink';
import toast from 'react-hot-toast';

interface ValidationResult {
  timestamp: string;
  totalContacts: number;
  scoresValidated: number;
  anomalies: Array<{
    contactId: string;
    email: string;
    score: number;
    issue: string;
  }>;
  metrics: {
    avgScore: number;
    medianScore: number;
    scoreDistribution: Record<string, number>;
    confidenceDistribution: Record<string, number>;
  };
  warnings: string[];
  status: 'passed' | 'warning' | 'failed';
}

export default function CIScoringValidation() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [sampleSize, setSampleSize] = useState(50);

  useEffect(() => {
    const unsubscribe = blink.auth.onAuthStateChanged((state) => {
      setUser(state.user);
    });
    return unsubscribe;
  }, []);

  const runValidation = async () => {
    if (!user) {
      toast.error('Please sign in to run validation');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        'https://lemon-ui-agentic-email-ai-agent-ben3ou87.functions.blink.new/ci-validation-job',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: user.id,
            sampleSize
          })
        }
      );

      if (!response.ok) {
        throw new Error('Validation failed');
      }

      const result = await response.json();
      setValidationResult(result);

      if (result.status === 'passed') {
        toast.success('Validation passed successfully');
      } else if (result.status === 'warning') {
        toast('Validation completed with warnings', { icon: '⚠️' });
      } else {
        toast.error('Validation failed - check results');
      }

    } catch (error) {
      console.error('Validation error:', error);
      toast.error('Failed to run validation');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'passed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Activity className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'passed':
        return 'bg-green-500/10 text-green-700 border-green-200';
      case 'warning':
        return 'bg-yellow-500/10 text-yellow-700 border-yellow-200';
      case 'failed':
        return 'bg-red-500/10 text-red-700 border-red-200';
      default:
        return 'bg-gray-500/10 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="container mx-auto px-6 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">CI Scoring Validation</h1>
        <p className="text-muted-foreground">
          Run comprehensive accuracy tests on Contact Intelligence scoring system
        </p>
      </div>

      {/* Controls */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Validation Controls</CardTitle>
          <CardDescription>
            Configure and run scoring accuracy validation
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">
                Sample Size
              </label>
              <input
                type="number"
                value={sampleSize}
                onChange={(e) => setSampleSize(Number(e.target.value))}
                min="10"
                max="200"
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">&nbsp;</label>
              <Button
                onClick={runValidation}
                disabled={loading || !user}
                className="w-full"
                size="lg"
              >
                {loading ? (
                  <>
                    <Activity className="mr-2 h-4 w-4 animate-spin" />
                    Running Validation...
                  </>
                ) : (
                  <>
                    <PlayCircle className="mr-2 h-4 w-4" />
                    Run Validation
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {validationResult && (
        <div className="space-y-6">
          {/* Status Overview */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Validation Status</CardTitle>
                  <CardDescription>
                    {new Date(validationResult.timestamp).toLocaleString()}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(validationResult.status)}
                  <Badge className={getStatusColor(validationResult.status)}>
                    {validationResult.status.toUpperCase()}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">
                    {validationResult.totalContacts}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Total Contacts
                  </div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">
                    {validationResult.scoresValidated}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Scores Validated
                  </div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold text-red-600">
                    {validationResult.anomalies.length}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Anomalies Found
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Warnings */}
          {validationResult.warnings.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <div className="font-semibold mb-2">Validation Warnings:</div>
                <ul className="list-disc list-inside space-y-1">
                  {validationResult.warnings.map((warning, i) => (
                    <li key={i}>{warning}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Metrics */}
          <div className="grid grid-cols-2 gap-6">
            {/* Score Metrics */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Score Metrics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium">Average Score</span>
                    <span className="text-sm font-bold">
                      {validationResult.metrics.avgScore.toFixed(1)}
                    </span>
                  </div>
                  <Progress value={validationResult.metrics.avgScore} />
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium">Median Score</span>
                    <span className="text-sm font-bold">
                      {validationResult.metrics.medianScore.toFixed(1)}
                    </span>
                  </div>
                  <Progress value={validationResult.metrics.medianScore} />
                </div>
              </CardContent>
            </Card>

            {/* Score Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Score Distribution
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(validationResult.metrics.scoreDistribution).map(([level, count]) => (
                  <div key={level} className="flex items-center justify-between">
                    <span className="text-sm font-medium capitalize">{level}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{count}</span>
                      <Badge variant="outline">
                        {((count / validationResult.scoresValidated) * 100).toFixed(0)}%
                      </Badge>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Anomalies */}
          {validationResult.anomalies.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  Scoring Anomalies ({validationResult.anomalies.length})
                </CardTitle>
                <CardDescription>
                  Contacts with unexpected or inconsistent scores
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {validationResult.anomalies.slice(0, 10).map((anomaly, i) => (
                    <div key={i}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium">{anomaly.email}</div>
                          <div className="text-sm text-muted-foreground">
                            {anomaly.issue}
                          </div>
                        </div>
                        <Badge variant="outline">
                          Score: {anomaly.score}
                        </Badge>
                      </div>
                      {i < validationResult.anomalies.length - 1 && (
                        <Separator className="mt-3" />
                      )}
                    </div>
                  ))}
                  {validationResult.anomalies.length > 10 && (
                    <div className="text-center text-sm text-muted-foreground pt-2">
                      + {validationResult.anomalies.length - 10} more anomalies
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Empty State */}
      {!validationResult && !loading && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Activity className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Validation Results</h3>
            <p className="text-muted-foreground text-center mb-6 max-w-md">
              Run a validation check to analyze scoring accuracy across your contacts
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
